import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ACCENT, COLORS, FONTS } from '../theme';
import type { SlotCooldownState } from '../Systems/ActionBar/cooldownDisplay';
import { useLiveCooldown, type LiveCooldown } from '../Systems/ActionBar/liveCooldown';

/**
 * CooldownOverlay — what a hotbar slot draws over its icon for the state
 * `slotCooldownState` resolved (src/game/ui/Systems/ActionBar/cooldownDisplay.ts).
 *
 *  - cooldown: the remaining part dimmed hard, a bright leading edge on the
 *    sweep, an outlined seconds countdown (player setting), and a short ready
 *    flash when it ends;
 *  - gcd: a light sweep only (it flickers after every cast);
 *  - recast: no sweep — a green ring, pips for the presses left, and the
 *    window draining along the bottom edge.
 *
 * Drawn above the icon (z 6) and below the key and mana labels (z 10).
 * Given `live`, it follows the slot's cooldown itself: it re-renders only when
 * the mode, the number or the pips change, and writes the sweep angle and the
 * recast bar to the DOM every frame (liveCooldown.ts) — never a React commit
 * per frame. Given only `state`, it draws that state (gallery, tests).
 */
interface CooldownOverlayProps {
  /** A fixed state to draw (used when `live` is absent). */
  state?: SlotCooldownState;
  /** The slot's live cooldown; takes precedence over `state`. */
  live?: LiveCooldown | null;
  /** Settings → Interface "Hotbar cooldown numbers". */
  showNumber: boolean;
  borderRadius?: number | string;
}

/** Ready flash length, ms. */
export const READY_FLASH_MS = 450;

const RECAST_GREEN = '#7dffa8';
/** Leading edge width, % of a turn. */
const EDGE = 1.6;

const layer: React.CSSProperties = { position: 'absolute', inset: 0, pointerEvents: 'none' };

const COOLDOWN_SHADE = 'rgba(6, 8, 14, 0.74)';
const COOLDOWN_EDGE = 'rgba(255, 240, 200, 0.95)';
const GCD_SHADE = `${COLORS.mana}55`;

function sweep(fraction: number, shade: string, edge: string | null): string {
  const elapsed = (1 - fraction) * 100;
  if (!edge) return `conic-gradient(transparent ${elapsed}%, ${shade} 0)`;
  const edgeEnd = Math.min(100, elapsed + EDGE);
  return `conic-gradient(transparent ${elapsed}%, ${edge} ${elapsed}% ${edgeEnd}%, ${shade} 0)`;
}

/** True for one READY_FLASH_MS after a real cooldown ends (never on mount, never after a GCD). */
function useReadyFlash(mode: SlotCooldownState['mode']): boolean {
  const wasCooling = useRef(mode === 'cooldown');
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    const cooling = mode === 'cooldown';
    if (wasCooling.current && !cooling && mode !== 'recast') setFlash(true);
    wasCooling.current = cooling;
  }, [mode]);
  useEffect(() => {
    if (!flash) return undefined;
    const timer = setTimeout(() => setFlash(false), READY_FLASH_MS);
    return () => clearTimeout(timer);
  }, [flash]);
  return flash;
}

/** The sweep's background for a mode (null when the mode has no sweep). */
export function sweepBackground(state: SlotCooldownState): string | null {
  if (state.mode === 'cooldown') return sweep(state.fraction, COOLDOWN_SHADE, COOLDOWN_EDGE);
  if (state.mode === 'gcd') return sweep(state.fraction, GCD_SHADE, null);
  // A usable remaining charge keeps its bright icon while a light sweep shows
  // the next serial recovery. It must keep animating through ready-to-ready.
  if (isChargeRecharging(state)) return sweep(state.fraction, GCD_SHADE, null);
  return null;
}

function isChargeRecharging(state: SlotCooldownState): boolean {
  return state.charges !== undefined && state.maxCharges !== undefined
    && state.charges < state.maxCharges && state.remainingMs > 0;
}

/** What changes the overlay's markup (everything but the sweep angle and bar width). */
function overlayKey(showNumber: boolean) {
  return (s: SlotCooldownState): string =>
    `${s.mode}|${s.mode === 'cooldown' && showNumber ? s.label : ''}|${s.castsLeft}|${isChargeRecharging(s)}`;
}

const READY_STATE: SlotCooldownState = { mode: 'ready', remainingMs: 0, fraction: 0, label: null, castsLeft: 0 };

export const CooldownOverlay: React.FC<CooldownOverlayProps> = ({ state: fixed, live = null, showNumber, borderRadius = 4 }) => {
  const liveState = useLiveCooldown(live, overlayKey(showNumber));
  const state = live ? liveState : (fixed ?? READY_STATE);
  const flash = useReadyFlash(state.mode);
  const sweepRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const renderedMode = useRef(state.mode);
  useLayoutEffect(() => { renderedMode.current = state.mode; });
  // Per frame: move the sweep and the recast bar in place (no React commit).
  useEffect(() => {
    if (!live) return undefined;
    return live.subscribe((s) => {
      if (s.mode !== renderedMode.current) return; // a re-render is on its way
      const background = sweepBackground(s);
      if (background && sweepRef.current) {
        sweepRef.current.style.background = background;
        sweepRef.current.dataset.fraction = s.fraction.toFixed(3);
      }
      if (s.mode === 'recast' && barRef.current) barRef.current.style.width = `${s.fraction * 100}%`;
    });
  }, [live]);
  if (state.mode === 'ready' && !flash && !isChargeRecharging(state)) return null;

  return (
    <div
      data-testid="cooldown-overlay"
      data-cooldown-mode={flash && state.mode === 'ready' ? 'ready-flash' : state.mode}
      style={{ ...layer, zIndex: 6, borderRadius, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {(state.mode === 'cooldown' || state.mode === 'gcd' || isChargeRecharging(state)) && (
        <div
          key={state.mode}
          ref={sweepRef}
          data-testid="cooldown-sweep"
          data-fraction={state.fraction.toFixed(3)}
          style={{ position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%',
            background: sweepBackground(state) ?? undefined }}
        />
      )}

      {state.mode === 'cooldown' && showNumber && state.label && (
        <div
          role="img"
          aria-label={`Available in ${state.label} seconds`}
          data-testid="cooldown-countdown"
          style={{
            position: 'relative',
            color: '#fff',
            fontFamily: FONTS.ui,
            fontSize: state.label.length > 3 ? 15 : 19,
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.5px',
            // Readable over any icon: a black outline plus a soft drop.
            WebkitTextStroke: '3px #000',
            paintOrder: 'stroke fill',
            textShadow: '0 0 4px #000, 0 1px 2px #000',
          }}
        >
          {state.label}
        </div>
      )}

      {state.mode === 'recast' && (
        <>
          <div style={{ ...layer, borderRadius, boxShadow: `inset 0 0 0 2px ${RECAST_GREEN}, inset 0 0 10px ${RECAST_GREEN}66` }} />
          <div
            role="img"
            aria-label={`Recast ready: ${state.castsLeft} left`}
            data-testid="cooldown-recast"
            style={{ position: 'absolute', top: 3, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 3 }}
          >
            {Array.from({ length: state.castsLeft }, (_, i) => (
              <span key={i} style={{ width: 6, height: 6, borderRadius: 3, background: RECAST_GREEN, boxShadow: '0 0 0 1px #000, 0 0 4px #000' }} />
            ))}
          </div>
          <div style={{ position: 'absolute', left: 3, right: 3, bottom: 3, height: 3, background: 'rgba(0,0,0,0.7)', borderRadius: 2 }}>
            <div ref={barRef} data-testid="cooldown-recast-bar" style={{ width: `${state.fraction * 100}%`, height: '100%', background: RECAST_GREEN, borderRadius: 2 }} />
          </div>
        </>
      )}

      {flash && state.mode === 'ready' && (
        <div
          data-testid="cooldown-ready-flash"
          style={{ ...layer, borderRadius, boxShadow: `inset 0 0 0 2px ${ACCENT.brassBright}, inset 0 0 14px ${ACCENT.brassBright}`,
            background: 'rgba(255, 244, 214, 0.35)', animation: `cooldown-ready-flash ${READY_FLASH_MS}ms ease-out forwards` }}
        />
      )}

      <style>{`
        @keyframes cooldown-ready-flash {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};
